import React, { useCallback, useMemo } from 'react';
import { Table as TableComponent } from '@trussworks/react-uswds';
import _sortBy from 'lodash/sortBy';
import Pagination, { defaultPageSizeOptions } from '@components/pagination/Pagination';
import TableHeaderCell from './TableHeaderCell';
import { useTableStore } from '@stores';
import classNames from 'classnames';

import './table.scss';

export type TableSortDirection = 'asc' | 'desc';

export type TableHeaderData = {
  label: string;
  name: string;
  className?: string;
  colSpan?: number;
  defaultSortDirection?: TableSortDirection;
  helpText?: React.ReactNode | string;
  isSortable?: boolean;
  rowSpan?: number;
  width?: string;
};

export type TableColumnAlignment = 'center' | 'right';

type RowCellContent = string | number | React.ReactNode;

type RowData = {
  id: number | string;
  [key: string]: RowCellContent;
};

type ColumnOrder = {
  name: string;
  alignment?: TableColumnAlignment;
};

type Props = {
  columnOrder: ColumnOrder[];
  headerData: TableHeaderData[][];
  id: string;
  rowData: RowData[];
  className?: string;
  noRecordsText?: string;
  onPageChange?: (pageNumber: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSortDirectionChange?: (sortDirection: TableSortDirection) => void;
  onSortNameChange?: (sortName: string) => void;
  pageSizeOptions?: number[];
  rightContent?: React.ReactNode;
  isScrollable?: boolean;
  rowKey?: string;
  secondarySort?: string;
  showPageSizeSelector?: boolean;
  sortColumn?: string;
  sortDirection?: TableSortDirection;
  totalRecords?: number;
};

const Table = ({
  className,
  columnOrder,
  headerData,
  id,
  noRecordsText = 'No Records to Display',
  onPageChange,
  onPageSizeChange,
  onSortDirectionChange,
  onSortNameChange,
  pageSizeOptions = defaultPageSizeOptions,
  rightContent,
  rowData,
  isScrollable,
  secondarySort,
  showPageSizeSelector,
  totalRecords,
}: Props) => {
  const scrollContainerStyles = classNames({
    'usa-table-container--scrollable': isScrollable,
  });

  const {
    getPageNumber,
    getPageSize,
    getSortColumn,
    getSortDirection,
    setPageNumber,
    setPageSize,
    setSortColumn,
    setSortDirection,
  } = useTableStore();

  const pageSize = getPageSize(id);
  const pageNumber = getPageNumber(id);
  const sortColumn = getSortColumn(id);
  const sortDirection = getSortDirection(id);

  const handleSortClick = useCallback(
    (name: string, direction: TableSortDirection, defaultSortDirection: TableSortDirection) => {
      const isNewSortColumn = name !== sortColumn;
      const isNewSortDirection = direction !== sortDirection;
      const newSortDirection = isNewSortColumn ? defaultSortDirection : direction; // reset the direction to the column default if changing the sort column

      if (isNewSortColumn) {
        setSortColumn(id, name);
        onSortNameChange?.(name);
      }
      if (isNewSortDirection) {
        setSortDirection(id, newSortDirection);
        onSortDirectionChange?.(newSortDirection);
      }
    },
    [id, onSortDirectionChange, onSortNameChange, setSortColumn, setSortDirection, sortColumn, sortDirection]
  );
  const handlePageChange = useCallback(
    (newPageNum: number) => {
      setPageNumber(id, newPageNum);
      onPageChange?.(newPageNum);
    },
    [id, onPageChange, setPageNumber]
  );
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageNumber(id, 1); // reset to first page when changing page size
      setPageSize(id, newPageSize);
      onPageSizeChange?.(newPageSize);
    },
    [id, onPageSizeChange, setPageNumber, setPageSize]
  );

  const sortedRowData = useMemo(() => {
    if (!rowData) return [];
    if (onSortNameChange || !sortColumn) return rowData;
    const sortedData = _sortBy(rowData, [sortColumn, secondarySort]);
    return sortDirection === 'asc' ? sortedData : sortedData.reverse();
  }, [rowData, onSortNameChange, secondarySort, sortColumn, sortDirection]);

  const pageCount = Math.ceil((totalRecords ?? sortedRowData.length) / pageSize);

  const paginatedRowData = useMemo(() => {
    if (onSortNameChange) return sortedRowData;

    const startIndex = (pageNumber - 1) * pageSize;
    return sortedRowData.slice(startIndex, startIndex + pageSize);
  }, [pageNumber, pageSize, onSortNameChange, sortedRowData]);

  return (
    <div className='table-wrapper'>
      <div className={scrollContainerStyles} tabIndex={isScrollable ? 0 : undefined}>
        <TableComponent bordered fullWidth compact className={className}>
          <thead>
            {headerData?.map((headerRow) => (
              <tr key={`header-${headerRow[0].label}`}>
                {headerRow?.map((headerCell) => (
                  <React.Fragment key={headerCell.label}>
                    <TableHeaderCell
                      onClick={handleSortClick}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      {...headerCell}
                    />
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paginatedRowData?.length === 0 && (
              <tr>
                <td className='no-table-records' colSpan={columnOrder.length}>
                  {noRecordsText}
                </td>
              </tr>
            )}
            {paginatedRowData?.map((row: RowData) => {
              const rowKey = row.rowKey || row.id;

              return (
                <tr key={`row-${rowKey}`}>
                  {columnOrder?.map((column) => {
                    const tdStyles = classNames('table-cell', {
                      [`text-${column.alignment}`]: column.alignment,
                    });
                    return (
                      <td className={tdStyles} key={`cell-${rowKey}-${column.name}`}>
                        {row[column.name]}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </TableComponent>
      </div>
      <Pagination
        currentPage={pageNumber}
        itemsPerPage={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageCount={pageCount}
        pageSizeOptions={pageSizeOptions}
        rightContent={rightContent}
        showPageSizeSelector={showPageSizeSelector}
      />
    </div>
  );
};

export default Table;
